import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Check, 
  ArrowRight, 
  Building2, 
  Users, 
  FileText, 
  Shield, 
  Clock, 
  Mail,
  Upload,
  BarChart3,
  Globe
} from 'lucide-react';

export default function PricingPage() {
  const features = [
    { icon: FileText, text: "Unlimited case management" },
    { icon: Upload, text: "Document upload & storage" },
    { icon: Users, text: "Client portal access" },
    { icon: Mail, text: "Automated email notifications" },
    { icon: BarChart3, text: "Reports & analytics" },
    { icon: Clock, text: "Biometrics reminders" },
    { icon: Shield, text: "Role-based access control" },
    { icon: Globe, text: "PDF form auto-fill" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="NewSlate" className="h-10 object-contain" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" className="font-body">Home</Button>
            </Link>
            <Link to="/login">
              <Button className="bg-primary text-white font-body" data-testid="header-signin-btn">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-body font-medium mb-6">
            Simple, Transparent Pricing
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-primary mb-6">
            One Plan. Everything Included.
          </h1>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            No hidden fees, no complicated tiers. Get full access to all NewSlate features 
            with straightforward pricing that scales with your team.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Setup Fee Card */}
            <Card className="border-2 border-border shadow-lg">
              <CardHeader className="text-center pb-2">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="font-display text-2xl text-primary">Organization Setup</CardTitle>
                <CardDescription className="font-body">One-time fee per organization</CardDescription>
              </CardHeader>
              <CardContent className="text-center pt-4">
                <div className="mb-6">
                  <span className="font-display text-5xl font-bold text-foreground">$99</span>
                  <span className="font-body text-muted-foreground ml-2">one-time</span>
                </div>
                <ul className="space-y-3 text-left mb-8">
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    Organization account creation
                  </li>
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    Initial admin user setup
                  </li>
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    Data migration assistance
                  </li>
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    Onboarding support call
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Monthly Fee Card */}
            <Card className="border-2 border-primary shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-accent text-white text-xs font-body font-medium px-3 py-1 rounded-bl-lg">
                Most Popular
              </div>
              <CardHeader className="text-center pb-2">
                <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="font-display text-2xl text-primary">Per User</CardTitle>
                <CardDescription className="font-body">Monthly subscription per user</CardDescription>
              </CardHeader>
              <CardContent className="text-center pt-4">
                <div className="mb-6">
                  <span className="font-display text-5xl font-bold text-foreground">$49</span>
                  <span className="font-body text-muted-foreground ml-2">/month</span>
                </div>
                <ul className="space-y-3 text-left mb-8">
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    Full platform access
                  </li>
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    Unlimited cases & clients
                  </li>
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    All features included
                  </li>
                  <li className="flex items-center gap-3 font-body text-sm">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    Priority email support
                  </li>
                </ul>
                <Link to="/login">
                  <Button className="w-full bg-primary text-white font-body" size="lg" data-testid="get-started-btn">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Example Calculation */}
          <div className="mt-12 bg-secondary/50 rounded-2xl p-6 md:p-8">
            <h3 className="font-display text-xl font-bold text-primary mb-4 text-center">
              Example: 5-User Team
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div className="bg-card rounded-xl p-4">
                <p className="font-body text-sm text-muted-foreground">Setup Fee</p>
                <p className="font-display text-2xl font-bold text-foreground">$99</p>
                <p className="font-body text-xs text-muted-foreground">one-time</p>
              </div>
              <div className="bg-card rounded-xl p-4">
                <p className="font-body text-sm text-muted-foreground">Monthly Cost</p>
                <p className="font-display text-2xl font-bold text-foreground">$245</p>
                <p className="font-body text-xs text-muted-foreground">5 users × $49/mo</p>
              </div>
              <div className="bg-primary/10 rounded-xl p-4 border-2 border-primary/20">
                <p className="font-body text-sm text-primary font-medium">First Month Total</p>
                <p className="font-display text-2xl font-bold text-primary">$344</p>
                <p className="font-body text-xs text-muted-foreground">then $245/mo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-card border-t border-border">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-3xl font-bold text-primary text-center mb-4">
            Everything You Need
          </h2>
          <p className="font-body text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Every plan includes full access to all features. No feature gating, no surprises.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-secondary/30 rounded-xl">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="font-body text-sm font-medium text-foreground">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-3xl font-bold text-primary text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Are there any long-term contracts?
              </h3>
              <p className="font-body text-muted-foreground">
                No, NewSlate is month-to-month. You can cancel anytime with no cancellation fees.
              </p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Can I add or remove users anytime?
              </h3>
              <p className="font-body text-muted-foreground">
                Yes! Add users as your team grows or remove them if needed. Billing adjusts automatically on your next cycle.
              </p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Is the client portal included?
              </h3>
              <p className="font-body text-muted-foreground">
                Yes, unlimited client portal access is included at no extra cost. Your clients can view their case status, upload documents, and complete their profiles.
              </p>
            </div>
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                What payment methods do you accept?
              </h3>
              <p className="font-body text-muted-foreground">
                We accept all major credit cards (Visa, Mastercard, American Express) and can arrange ACH transfers for annual plans.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Simplify Your Practice?
          </h2>
          <p className="font-body text-white/80 mb-8 max-w-xl mx-auto">
            Join immigration firms already using NewSlate to manage their cases more efficiently.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-card text-primary hover:bg-white/90 font-body" data-testid="cta-get-started">
                Get Started Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-body">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 bg-card rounded-lg flex items-center justify-center">
              <span className="text-primary font-bold">N</span>
            </div>
            <span className="font-display text-lg font-bold">NewSlate</span>
          </div>
          <p className="font-body text-sm text-slate-400">
            © {new Date().getFullYear()} NewSlate. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
